import { MessageType } from "components/forms/message-view";
import { FC, useMemo } from "react";
import ReactQuill from "react-quill";
import { useTranslation } from "react-i18next";

interface ExtendedMessageType extends MessageType {
    is_condition?: boolean;
}

export const EnhancedMessageView: FC<{
    messages: ExtendedMessageType[];
    form: any;
}> = ({ messages, form }) => {
    const { t } = useTranslation();

    const quillModules = useMemo(
        () => ({
            toolbar: [
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["clean"],
            ],
        }),
        [],
    );

    const quillFormats = useMemo(
        () => ["bold", "italic", "underline", "strike", "list", "bullet"],
        [],
    );

    const sortedMessages = useMemo(() => {
        return [...messages].sort((a, b) => {
            if (a.is_condition && !b.is_condition) return -1;
            if (!a.is_condition && b.is_condition) return 1;
            return 0;
        });
    }, [messages]);

    const renderMessage = (message: ExtendedMessageType) => (
        <div
            key={message.uuid}
            className={`p-3 rounded-lg ${
                message.is_condition
                    ? "bg-amber-50 border border-amber-200 mr-6"
                    : message.state === "INTERNAL"
                      ? "bg-blue-50 border border-blue-100 ml-6"
                      : "bg-gray-50 border border-gray-100 mr-6"
            }`}>
            <div className="flex justify-between items-start mb-1">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                        {message.sender_name
                            .split(" ")
                            .map(
                                (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase(),
                            )
                            .join(" ")}
                    </span>
                    {message.is_condition && (
                        <span className="bg-amber-200 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
                            Condition Message
                        </span>
                    )}
                </div>
                <span className="text-xs text-gray-500">
                    {new Date(message.timestamp).toLocaleString()}
                </span>
            </div>
            <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: message.message }}
            />
        </div>
    );

    return (
        <div className="space-y-4">
            {messages.length === 0 ? (
                <div className="text-center py-10">
                    <p className="text-gray-500">{t("messages.no_messages")}</p>
                </div>
            ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto px-1">
                    {sortedMessages.map(renderMessage)}
                </div>
            )}

            <form onSubmit={form.handleSubmit} className="mt-4">
                <div className="rounded-md border border-gray-300 overflow-hidden">
                    <style>{`
            .ql-editor {
              min-height: 80px !important;
            }
          `}</style>
                    <ReactQuill
                        value={form.values.message}
                        onChange={(value) =>
                            form.setFieldValue("message", value)
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder={t("forms.placeholders.type_message")}
                        style={{
                            backgroundColor: "white",
                        }}
                    />
                </div>
            </form>
        </div>
    );
};
